import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { Session } from "@supabase/supabase-js";
import { mutate } from "swr";
import { supabaseClient } from "@/lib/supabase/client";
import { forgetLastScope } from "@/lib/last-user";

export type AuthState = {
  session: Session | null;
  /** `true` until supabase-js has read (and, on `/auth/callback`, exchanged) the stored session. */
  loading: boolean;
};

const AuthContext = createContext<AuthState | undefined>(undefined);

/**
 * Mirror of the Supabase session. `onAuthStateChange` emits `INITIAL_SESSION`
 * as soon as the client has restored the session from localStorage, so the
 * guards never see a false "signed out".
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    session: null,
    loading: true,
  });

  useEffect(() => {
    const {
      data: { subscription },
    } = supabaseClient.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") {
        // Nothing of the previous account may leak into the next one: per-user
        // localStorage keys and every SWR entry.
        forgetLastScope();
        void mutate(() => true, undefined, { revalidate: false });
      }
      setState({ session, loading: false });
    });
    return () => subscription.unsubscribe();
  }, []);

  return <AuthContext.Provider value={state}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
